import { useState, useCallback } from 'react';

const CF = 'https://speed.cloudflare.com';

interface SpeedTestResults {
  ping: number;
  jitter: number;
  loss: number;
  download: number;
  upload: number;
  bloat: number;
  dns: number;
  bestStream: string;
  timestamp: Date;
}

interface TestState {
  running: boolean;
  phase: string;
  progress: number;
  ping: number;
  download: number;
  upload: number;
  jitter: number;
  loss: number;
  bloat: number;
  dns: number;
  results: SpeedTestResults | null;
}

export function useSpeedTest() {
  const [state, setState] = useState<TestState>({
    running: false,
    phase: 'READY',
    progress: 0,
    ping: 0,
    download: 0,
    upload: 0,
    jitter: 0,
    loss: 0,
    bloat: 0,
    dns: 0,
    results: null,
  });

  const updateState = (updates: Partial<TestState>) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  const doPing = useCallback(async (samples = 12) => {
    const pings: number[] = [];
    updateState({ phase: 'PING TEST', progress: 5 });

    for (let i = 0; i < samples; i++) {
      const t0 = performance.now();
      try {
        const ctrl = new AbortController();
        const tid = setTimeout(() => ctrl.abort(), 3000);
        await fetch(`${CF}/__down?bytes=1&_=${Date.now()}${i}`, {
          cache: 'no-store',
          signal: ctrl.signal,
        });
        clearTimeout(tid);
        pings.push(performance.now() - t0);
      } catch {}
      await new Promise(r => setTimeout(r, 100));
    }

    if (pings.length === 0) return { ping: 999, jitter: 999, loss: 100 };

    pings.sort((a, b) => a - b);
    const trim = pings.slice(1, -1);
    const avg = trim.reduce((a, b) => a + b) / trim.length;
    const jitter = Math.max(...trim) - Math.min(...trim);
    const loss = ((samples - pings.length) / samples) * 100;

    return { ping: avg, jitter, loss };
  }, []);

  const doDns = useCallback(async () => {
    const hosts = ['cloudflare.com', 'google.com', 'github.com'];
    const results: number[] = [];

    for (const h of hosts) {
      const t0 = performance.now();
      try {
        await fetch(`https://${h}/favicon.ico?_=${Date.now()}`, {
          cache: 'no-store',
          mode: 'no-cors',
        });
      } catch {}
      results.push(performance.now() - t0);
      await new Promise(r => setTimeout(r, 80));
    }

    return results.reduce((a, b) => a + b) / results.length;
  }, []);

  const doDownload = useCallback(
    async (onSample: (mbps: number) => void) => {
      const DL_BYTES = [100000, 1000000, 5000000, 10000000, 25000000];
      const results: number[] = [];

      for (let i = 0; i < DL_BYTES.length; i++) {
        const bytes = DL_BYTES[i];
        const url = `${CF}/__down?bytes=${bytes}&_=${Date.now()}`;
        const t0 = performance.now();
        let loaded = 0;

        try {
          const r = await fetch(url, { cache: 'no-store' });
          const reader = r.body!.getReader();
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            loaded += value.byteLength;
            const mbps = (loaded * 8) / ((performance.now() - t0) / 1000 / 1e6);
            onSample(mbps);
          }
        } catch {
          const r = await fetch(url, { cache: 'no-store' });
          await r.arrayBuffer();
          loaded = bytes;
        }

        const mbps = (loaded * 8) / ((performance.now() - t0) / 1000 / 1e6);
        results.push(mbps);
        updateState({ progress: 10 + ((i + 1) / DL_BYTES.length) * 40 });
        await new Promise(r => setTimeout(r, 80));
      }

      results.sort((a, b) => a - b);
      const trimmed = results.slice(
        Math.floor(results.length * 0.4),
        Math.ceil(results.length * 0.6)
      );
      return trimmed.reduce((a, b) => a + b) / trimmed.length;
    },
    []
  );

  const doUpload = useCallback(
    async (onSample: (mbps: number) => void) => {
      const UL_BYTES = [100000, 500000, 2000000, 5000000, 10000000];
      const results: number[] = [];

      for (let i = 0; i < UL_BYTES.length; i++) {
        const bytes = UL_BYTES[i];
        const data = new Uint8Array(bytes).fill(0x41);
        const t0 = performance.now();

        try {
          await fetch(`${CF}/__up?_=${Date.now()}`, {
            method: 'POST',
            body: data,
            cache: 'no-store',
            headers: { 'Content-Type': 'application/octet-stream' },
          });
        } catch {}

        const mbps = (bytes * 8) / ((performance.now() - t0) / 1000 / 1e6);
        results.push(mbps);
        onSample(mbps);
        updateState({ progress: 55 + ((i + 1) / UL_BYTES.length) * 40 });
        await new Promise(r => setTimeout(r, 80));
      }

      results.sort((a, b) => a - b);
      const trimmed = results.slice(
        Math.floor(results.length * 0.4),
        Math.ceil(results.length * 0.6)
      );
      return trimmed.reduce((a, b) => a + b) / trimmed.length;
    },
    []
  );

  return {
    state,
    updateState,
    doPing,
    doDns,
    doDownload,
    doUpload,
  };
}
